import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme'

import { List } from '@independent-software/typeui/controls/List';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Form } from '@independent-software/typeui/controls/Form';
import { Input } from '@independent-software/typeui/controls/Input';
import { Button } from '@independent-software/typeui/controls/Button';

import { Category } from './Category';

type TMode = 'none' | 'add' | 'edit' | 'delete';

interface ICategoryItem {
  id?: number;
  parent: number;
  name: string;
}

interface IProps {
  /** Tree of items to show. Each item must have a parent (numeric) that points
   *  to its parent, or null. */
  items: ICategoryItem[];
  /**
   * Fired when item is clicked.
   */
  onClick?: (item: ICategoryItem) => void;
  /**
   * Fired when a new item is added to a parent.
   */
  onAdd?: (item: ICategoryItem, name: string) => void;
  /**
   * Fired when an item is edited.
   */
  onEdit?: (item: ICategoryItem, name: string) => void;
  /**
   * Fired when an item is deleted.
   */
  onDelete?: (item: ICategoryItem) => void;
  /**
   * If a child count must be shown, the name of the counter
   * field (e.g. stations_count) must be provided.
   */
  countfield?: string;
  /**
   * Array of items IDs that are active, to be highlighted
   * in the tree.
   */
  active?: number[];
}

const CategoryTree = (props: IProps) => {
  const [mode, setMode] = React.useState('none' as TMode);
  const [open, setOpen] = React.useState(new Set<number>());
  const [current, setCurrent] = React.useState(null as ICategoryItem);
  const [name, setName] = React.useState('');


  const isActive = (id: number) => {
    return props.active && props.active.includes(id);
  }

  const toggleItem = (id: number) => {
    if(open.has(id)) {
      open.delete(id);
    } else {
      open.add(id);
    }
    setOpen(open);
  }

  const handleChangeName = (_name: string) => {
    setName(_name);
  }  

  const handleBeginAdd = (item: ICategoryItem) => {
    if(item) {
      open.add(item.id);
      setOpen( open );
    }
    setCurrent(item);
    setName('');
    setMode('add');
  }

  const handleEndAdd = () => {
    setMode('none');
    props.onAdd(current, name);
  }

  const handleBeginEdit = (item: ICategoryItem) => {
    setCurrent(item);
    setName(item.name);
    setMode('edit');
  }

  const handleEndEdit = () => {
    setMode('none');
    props.onEdit(current, name);
  }  

  const handleBeginDelete = (item: ICategoryItem) => {
    setCurrent(item);
    setMode('delete');
  }

  const handleEndDelete = () => {
    setMode('none');
    props.onDelete(current);
  }

  const renderItem = (item: ICategoryItem): React.ReactNode => {
    let p = props;
    // Does this item have any children?
    let hasChildren = props.items
      .some((sc) => sc.parent == item.id);
    // Is item currently open?
    let isOpen = open.has(item.id);

    return (
      <List.Item key={item.id}>
        <IconHolder>
          {hasChildren && <Icon name="caret-down" rotated={isOpen ? 0 : -90} onClick={() => toggleItem(item.id)}/>}
          {!hasChildren && <Icon name="circle" color="#fff"/>}
        </IconHolder>
        <List.Content>
          <Category 
            active={isActive(item.id)}
            onAdd={p.onAdd ? () => handleBeginAdd(item) : null} 
            onEdit={p.onEdit ? () => handleBeginEdit(item) : null} 
            onDelete={p.onDelete ? () => handleBeginDelete(item) : null} 
            onClick={props.onClick ? () => props.onClick(item) : null}>
              {item.name}
              {p.countfield && (item as any)[p.countfield] > 0 && <Count>{(item as any)[p.countfield]}</Count>}
          </Category>
          {isOpen && renderList(item.id)}
        </List.Content>
      </List.Item>
    );
  }

  const renderList = (parent: number = null): React.ReactNode => {
    return (
      <List relaxed>
        {props.items
          .filter(item => item.parent == parent)
          .map(item => renderItem(item))}
      </List>
    );
  }

  return (
    <>
      {renderList()}
      
      {props.onAdd && <ButtonHolder>
        <Button icon primary size="small" onClick={() => handleBeginAdd(null)}><Icon name="plus"/>Add</Button>
      </ButtonHolder>}

      <Dialog open={mode == 'add'} onClose={() => setMode('none')}>
        <Dialog.Content>
          <Form.Uncontrolled label="Category name" hint="Please enter a category name.">
            <Input value={name} type="text" placeholder="Name" fluid onChange={handleChangeName}/>
          </Form.Uncontrolled>
        </Dialog.Content>
        <Dialog.Footer>
          <Button secondary onClick={() => setMode('none')}>Cancel</Button>
          <Button primary disabled={name.length == 0} onClick={handleEndAdd}>Save</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={mode === 'edit'} onClose={() => setMode('none')}>
        <Dialog.Content>
          <Form.Uncontrolled label="Category name" hint="Please enter a category name.">
            <Input value={name} type="text" placeholder="Name" fluid onChange={handleChangeName}/>
          </Form.Uncontrolled>
        </Dialog.Content>
        <Dialog.Footer>
          <Button secondary onClick={() => setMode('none')}>Cancel</Button>
          <Button primary disabled={name.length == 0} onClick={handleEndEdit}>Update</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={mode === 'delete'} onClose={() => setMode('none')}>
        <Dialog.Content>
          <p>
            Are you sure you wish to <strong>delete</strong> this category?
          </p>
          <p>
            <strong>This will also delete all subcategories.</strong>
          </p>
        </Dialog.Content>
        <Dialog.Footer>
          <Button secondary onClick={() => setMode('none')}>Cancel</Button>
          <Button negative onClick={handleEndDelete}>Delete</Button>
        </Dialog.Footer>
      </Dialog>        

    </>
  );
}

const IconHolder = styled.div`
  padding-top: 2px;
`

const ButtonHolder = styled.div`
  padding: 6px 0 0 28px;
`

const Count = styled.div`
  display: inline-block;
  font-size: 80%;
  margin-left: 8px;
  color: #fff;
  background: #666;
  padding: 0 6px 0 6px;
  line-height: 1.3em;
`

export { CategoryTree }
